import {Box, Slide, useScrollTrigger} from '@mui/material';
import {BoxProps} from '@jobseeker/lotus/foundations';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import {useEffect, useState} from 'react';
import {handleTracking, deferTask} from '@jobseeker/common-core/utils';
import {AppState} from '@jobseeker/common-core/types';
import {shallowEqual, useSelector} from 'react-redux';
import cookie from 'cookie';
import {useWrappedTheme} from '@jobseeker/lotus/hooks/common';
import Loader from '@jobseeker/lotus/components/Loader';

import ProChip from '@jobseeker/lotus/elements/Pro';
import type {Theme} from '@mui/material';

function hashCode(str: string) {
  let hash = 0,
    i,
    chr;
  if (str?.length === 0) return hash;
  for (i = 0; i < str?.length; i++) {
    chr = str?.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash;
}

type FloatingApplyButtonProps = {
  theme: Theme;
  style: BoxProps;
  disabled?: boolean;
  loading?: boolean;
  onApply?: () => string | void;
  isPro?: boolean;
  openInsightsDialog?: () => void;
  show?: boolean;
  isProLoading?: boolean;
};

const FloatingApplyButton = ({
  theme,
  style = {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  disabled,
  loading = false,
  onApply,
  isPro,
  openInsightsDialog,
  show,
  isProLoading,
}: FloatingApplyButtonProps) => {
  const wrappedTheme = useWrappedTheme();
  const [cookieValue, setCookieValue] = useState('');

  useEffect(() => {
    const cookies = cookie.parse(document.cookie);
    const myCookieValue = cookies['PHPSESSID'];
    setCookieValue(myCookieValue);
  }, []);

  const PHPSESSID = Math.abs(hashCode(cookieValue)) % 2;

  sessionStorage.setItem('phpSessId', String(PHPSESSID));
  const ABTesting = PHPSESSID === 1;

  const page = useSelector((state: AppState) => state.app.page, shallowEqual);

  const sendEvent = (action: string) => {
    handleTracking(`Floating_Apply_Btn_${action}`, {
      event_category: 'Floating_Apply_Button',
      Origin: page,
      UserId: sessionStorage.getItem('userId') || 'NA',
      Device: sessionStorage.getItem('device') || 'desktop',
      Device_Type: sessionStorage.getItem('deviceType') || 'web',
      Status: sessionStorage.getItem('status') || 'LoggedOut',
      Php_SessionID_Mod: sessionStorage.getItem('phpSessId') || '0',
    });
  };

  if (disabled) return null;

  return (
    <Slide in={show} direction="up">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          position: 'fixed',
          bottom: '0px',
          width: '100%',
          zIndex: 12,
          gap: 2,
          ml: -2,
          backgroundColor: '#F3F7FA',
          borderBottomRightRadius: 16,
          borderBottomLeftRadius: 16,
          boxShadow:
            '0 2px 2px 0 rgba(0, 0, 0, 0.08), 0 6.48px 6px 0 rgba(41, 41, 41, 0.06) inset',
        }}
      >
        <Button
          sx={{
            width: {xxs: '130px'},
            borderColor: 'primary',
            display: 'flex',
            gap: 0.5,
            fontSize: wrappedTheme.typography.pxToRem(16),
            fontWeight: '500',
            my: {xxs: 2.5, md: 2.5},
            px: 4,
            py: 0.825,
            height: 37.5,
          }}
          variant={'outlined'}
          size="large"
          color="primary"
          onClick={openInsightsDialog}
        >
          {!isPro && (
            <ProChip
              key={'Pro0'}
              bgColor="linear-gradient(180deg, #FFB743 0%, #F99914 100%)"
            />
          )}
          Insights
        </Button>
        <Button
          sx={{
            fontSize: wrappedTheme.typography.pxToRem(16),
            fontWeight: '500',
            width: {xxs: '194px'},
            background: '#000',
            my: {xxs: 2.5, md: 2.5},
            px: 4,
            py: 1,
            height: 'fit-content',
            backgroundColor: 'secondary.main',
          }}
          variant="contained"
          size="small"
          disabled={loading}
          onClick={() => {
            if (loading) return;
            deferTask(() => {
              sendEvent('Click');
              onApply && onApply();
            });
          }}
          data-testid="floating_Apply_cta"
          data-track="apply-job"
        >
          {loading ? <Loader sx={{color: '#fff'}} size={24} /> : 'Apply'}
        </Button>
      </Box>
    </Slide>
  );
};

export default FloatingApplyButton;
